'use client'

import classNames from 'classnames'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Category } from '@/app/types/blog'

interface Props {
	categories: Category[]
	baseBlogPath: string
}

const CategoryChipsFilter: FC<Props> = ({ categories, baseBlogPath }) => {
	const t = useTranslations('Blog')
	const router = useRouter()
	const searchParams = useSearchParams()

	const activeCategory = searchParams.get('category')
	const isAllActive = !activeCategory
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [showLeftFade, setShowLeftFade] = useState(false)

	const handleScroll = () => {
		if (containerRef.current) {
			const scrollLeft = containerRef.current.scrollLeft
			const shouldShow = scrollLeft > 40
			setShowLeftFade(shouldShow)
		}
	}

	useEffect(() => {
		// Check initial scroll state
		handleScroll()
	}, [])

	const handleCategoryClick = useCallback(
		(categorySlug: string | null) => {
			const params = new URLSearchParams(searchParams.toString())

			if (categorySlug) {
				params.set('category', categorySlug)
			} else {
				params.delete('category')
			}

			const queryString = params.toString()
			const newUrl = queryString
				? `${baseBlogPath}?${queryString}`
				: baseBlogPath

			router.push(newUrl, { scroll: false })
		},
		[searchParams, baseBlogPath, router]
	)

	return (
		<div className="category-chips-wrapper">
			<div
				className="category-chips"
				ref={containerRef}
				onScroll={handleScroll}
			>
				{/* "All" chip - always first */}
				<button
					type="button"
					onClick={() => handleCategoryClick(null)}
					className={classNames('category-chips__chip category-chips__chip--all', {
						'category-chips__chip--active': isAllActive,
					})}
					aria-pressed={isAllActive}
				>
					{t('allCategories')}
				</button>

				{/* All categories in their original order */}
				{categories.map((category) => {
					const isActive = category.slug.current === activeCategory
					return (
						<button
							type="button"
							key={category._id}
							onClick={() => handleCategoryClick(category.slug.current)}
							className={classNames('category-chips__chip', {
								'category-chips__chip--active': isActive,
							})}
							aria-pressed={isActive}
						>
							{category.title}
						</button>
					)
				})}
			</div>
			<div
				className={classNames('category-chips__fade category-chips__fade--left', {
					'category-chips__fade--left--visible': showLeftFade,
				})}
				aria-hidden
			/>
			<div className="category-chips__fade" aria-hidden />
		</div>
	)
}

export default CategoryChipsFilter
